(() => {
  const EDGE_TOLERANCE = 3;

  const initSlider = (slider) => {
    const track = slider.querySelector('[data-accessories-track]');
    const previousButton = slider.querySelector('[data-accessories-prev]');
    const nextButton = slider.querySelector('[data-accessories-next]');

    if (!track || !previousButton || !nextButton) return;

    const setButtonVisibility = (button, visible) => {
      button.classList.toggle('is-hidden', !visible);
      button.setAttribute('aria-hidden', String(!visible));
      button.tabIndex = visible ? 0 : -1;
    };

    const updateNavigation = () => {
      const maximumScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
      const hasOverflow = maximumScroll > EDGE_TOLERANCE;

      setButtonVisibility(
        previousButton,
        hasOverflow && track.scrollLeft > EDGE_TOLERANCE
      );
      setButtonVisibility(
        nextButton,
        hasOverflow && track.scrollLeft < maximumScroll - EDGE_TOLERANCE
      );
    };

    const scrollAmount = () => Math.max(track.clientWidth * 0.9, 200);

    previousButton.addEventListener('click', () => {
      track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });

    nextButton.addEventListener('click', () => {
      track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });

    let scrollFrame;
    track.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(updateNavigation);
    }, { passive: true });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(updateNavigation);
      resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', updateNavigation);
    }

    slider.cd13UpdateNavigation = updateNavigation;
    window.requestAnimationFrame(updateNavigation);
  };

  const initAccessories = (root) => {
    if (!root || root.dataset.accessoriesReady === 'true') return;
    root.dataset.accessoriesReady = 'true';

    const panels = {
      necessary: root.querySelector('[data-accessories-panel="necessary"]'),
      recommended: root.querySelector('[data-accessories-panel="recommended"]'),
    };

    const sliders = [...root.querySelectorAll('.cd13-accessories__slider')];
    sliders.forEach(initSlider);

    const json = root.querySelector('[data-accessories-json]');
    if (!json) return;

    let data = {};
    try {
      data = JSON.parse(json.textContent || '{}');
    } catch (error) {
      root.hidden = true;
      return;
    }

    const updateLayout = () => {
      const visiblePanels = Object.values(panels).filter((panel) => panel && !panel.hidden).length;
      root.dataset.visiblePanels = String(visiblePanels);
      root.hidden = visiblePanels === 0;

      window.requestAnimationFrame(() => {
        sliders.forEach((slider) => slider.cd13UpdateNavigation?.());
      });
    };

    const applyVariant = (variantId) => {
      const entry = data[String(variantId)];
      if (!entry) {
        root.hidden = true;
        return;
      }

      Object.entries(panels).forEach(([key, panel]) => {
        if (!panel) return;

        const track = panel.querySelector('[data-accessories-track]');
        const html = String(entry[key] || '').trim();

        if (!track || !html) {
          panel.hidden = true;
          return;
        }

        track.innerHTML = html;
        track.scrollLeft = 0;
        panel.hidden = false;
      });

      updateLayout();
    };

    const productRoot = root.closest('[data-product-root]') || document;
    const idInput = productRoot.querySelector('[data-variant-id-input]');
    const optionGroups = [...productRoot.querySelectorAll('[data-option-position]')]
      .sort((first, second) => Number(first.dataset.optionPosition) - Number(second.dataset.optionPosition));

    const currentVariantId = () => {
      if (optionGroups.length === 0) return idInput?.value;

      const variantsJson = productRoot.querySelector('[data-product-variants-json]');
      if (!variantsJson) return idInput?.value;

      let variants = [];
      try {
        variants = JSON.parse(variantsJson.textContent || '[]');
      } catch (error) {
        return idInput?.value;
      }

      const options = optionGroups.map((group) => (
        group.querySelector('[data-option-value]:checked')?.value || ''
      ));
      const match = variants.find((variant) => (
        variant.options.every((value, index) => value === options[index])
      ));

      return match ? match.id : idInput?.value;
    };

    optionGroups.forEach((group) => {
      group.addEventListener('change', (event) => {
        if (!event.target.matches('[data-option-value]')) return;
        const variantId = currentVariantId();
        if (variantId) applyVariant(variantId);
      });
    });

    productRoot.addEventListener('cd13:variant-change', (event) => {
      const variantId = event.detail?.variant?.id;
      if (variantId) applyVariant(variantId);
    });

    const initialVariantId = currentVariantId();
    if (initialVariantId) {
      applyVariant(initialVariantId);
    } else {
      updateLayout();
    }
  };

  const init = (scope = document) => {
    const roots = scope.matches?.('[data-accessories-root]')
      ? [scope]
      : scope.querySelectorAll('[data-accessories-root]');

    roots.forEach(initAccessories);
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
